import { useEffect, useRef, useState, type ReactNode } from "react";
import { GITHUB_OSS, docsUrl } from "@/lib/hosts";

/**
 * Adapted from 21st.dev mini-navbar demo.
 * OpenGateway brand: logo, orange/purple CTAs, product nav links.
 */

function AnimatedNavLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) {
  // Dual-line slide needs fixed line box height or both labels show at once.
  return (
    <a
      href={href}
      className="group relative inline-block h-5 overflow-hidden text-sm leading-5"
    >
      <span className="flex flex-col transition-transform duration-300 ease-out group-hover:-translate-y-5">
        <span className="block h-5 whitespace-nowrap text-zinc-400">
          {children}
        </span>
        <span className="block h-5 whitespace-nowrap text-white">{children}</span>
      </span>
    </a>
  );
}

export function MiniNavbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [headerShapeClass, setHeaderShapeClass] = useState("rounded-full");
  const shapeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (shapeTimeoutRef.current) clearTimeout(shapeTimeoutRef.current);
    if (isOpen) {
      setHeaderShapeClass("rounded-2xl");
    } else {
      shapeTimeoutRef.current = setTimeout(() => {
        setHeaderShapeClass("rounded-full");
      }, 300);
    }
    return () => {
      if (shapeTimeoutRef.current) clearTimeout(shapeTimeoutRef.current);
    };
  }, [isOpen]);

  const navLinks = [
    { label: "Features", href: "#features" },
    { label: "How it works", href: "#how" },
    { label: "Pricing", href: "#pricing" },
  ];

  return (
    <header
      className={`fixed top-5 left-1/2 z-50 flex w-[calc(100%-1.5rem)] max-w-3xl -translate-x-1/2 flex-col items-center border border-white/10 bg-[#1f1f1f]/80 px-3 py-2 backdrop-blur-xl sm:w-auto sm:px-4 ${headerShapeClass} transition-[border-radius] duration-300`}
    >
      <div className="flex h-9 w-full items-center justify-between gap-x-4 sm:gap-x-6">
        <a href="/" className="flex shrink-0 items-center gap-2">
          <img
            src="/og-logo.png"
            alt="OpenGateway"
            className="h-7 w-7 rounded-lg shadow-md shadow-orange-500/25"
            width={28}
            height={28}
          />
          <span className="hidden text-sm font-semibold tracking-tight text-white sm:inline">
            OpenGateway
          </span>
        </a>

        <nav className="hidden items-center gap-x-5 text-sm sm:flex">
          {navLinks.map((link) => (
            <AnimatedNavLink key={link.href} href={link.href}>
              {link.label}
            </AnimatedNavLink>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-2 sm:flex">
          <a
              href={docsUrl("/")}
              className="inline-flex h-8 items-center rounded-full border border-white/12 bg-white/[0.04] px-3.5 text-sm font-medium text-zinc-300 transition hover:border-orange-500/40 hover:text-white"
            >
              Docs
            </a>
            <a
              href={GITHUB_OSS}
              target="_blank"
              rel="noopener noreferrer"
              className="og-btn-primary inline-flex h-8 items-center rounded-full px-3.5 text-sm"
            >
              Get started
            </a>
        </div>

        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center text-zinc-300 sm:hidden"
          onClick={() => setIsOpen((v) => !v)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      <div
        className={`flex w-full flex-col items-center overflow-hidden transition-all duration-300 ease-in-out sm:hidden ${
          isOpen
            ? "max-h-[480px] opacity-100 pt-4"
            : "pointer-events-none max-h-0 opacity-0 pt-0"
        }`}
      >
        <nav className="flex w-full flex-col items-center space-y-3 text-sm">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="w-full text-center text-zinc-300 transition hover:text-white"
              onClick={() => setIsOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="mt-4 flex w-full flex-col items-stretch gap-2">
          <a
              href={docsUrl("/")}
              className="w-full rounded-full border border-white/12 py-2 text-center text-sm text-zinc-300"
              onClick={() => setIsOpen(false)}
            >
              Docs
            </a>
            <a
              href={GITHUB_OSS}
              target="_blank"
              rel="noopener noreferrer"
              className="og-btn-primary w-full rounded-full py-2 text-center text-sm"
              onClick={() => setIsOpen(false)}
            >
              Get started
            </a>
        </div>
      </div>
    </header>
  );
}

export { MiniNavbar as Navbar };
